<template>
  <div
    class="flex sm:flex-row flex-col flex-wrap sm:mt-0 mt-4 px-4 sm:items-baseline sm:justify-between"
  >
    <div class="text-sm sm:text-left text-center sm:order-2 order-2">
      <p class="text-sm text-gray-400">
        {{ translate('vtable.showing', props.pageLang) }} {{ getCurrentStartRow() }}
        {{ translate('vtable.to', props.pageLang) }} {{ getCurrentEndRow() }}
        {{ translate('vtable.of', props.pageLang) }} {{ props.totalRows }}
        {{ translate('vtable.row', props.pageLang) }}
      </p>
    </div>
    <ul
      class="flex items-center sm:justify-start justify-center sm:justify-self-end space-x-1 rtl:space-x-reverse my-4 sm:order-2 order-1"
    >
      <li>
        <button
          type="button"
          class="flex justify-center disabled:bg-secondary-200 disabled:cursor-not-allowed items-center flex-shrink-0 font-semibold w-8 h-8 rounded-full transition bg-secondary-800 text-white hover:bg-secondary-800"
          :disabled="currentPage == 1"
          @click="prevPage"
        >
          <component
            :is="props.pageLang === 'en' ? ChevronLeft : ChevronRight"
            :size="14"
            :class="`text-secondary-50 ${currentPage === 1 && 'text-secondary-600'}`"
          ></component>
        </button>
      </li>

      <li
        v-for="page in [...Array(Math.min(2, totalPages)).keys()].map(x => x + 1)"
        :key="page"
      >
        <button
          type="button"
          class="flex justify-center flex-shrink-0 items-center font-semibold h-8 w-8 rounded-full transition"
          :class="{
            'bg-secondary-500 text-secondary-800 hover:text-white hover:bg-secondary-800': page === currentPage,
            'text-white bg-secondary-800 hover:text-secondary-800 hover:bg-secondary-500': page !== currentPage,
          }"
          @click="changePage(page)"
        >
          {{ page }}
        </button>
      </li>
      <li v-if="totalPages > 4"> ... </li>
      <li
        v-for="page in getMiddlePagination()"
        :key="page"
      >
        <button
          type="button"
          class="flex justify-center flex-shrink-0 items-center font-semibold w-8 h-8 rounded-full transition"
          :class="{
            'bg-secondary-500 text-secondary-800 hover:text-white hover:bg-secondary-800': page === currentPage,
            'text-white bg-secondary-800 hover:text-secondary-800 hover:bg-secondary-500': page !== currentPage,
          }"
          @click="changePage(page)"
        >
          {{ page }}
        </button>
      </li>
      <li v-if="totalPages > 4"> ... </li>
      <li
        v-for="page in (totalPages == 3? [3]: [totalPages - 1, totalPages])"
        v-show="totalPages >= 3"
        :key="page"
      >
        <button
          type="button"
          class="flex justify-center flex-shrink-0 items-center font-semibold w-8 h-8 rounded-full transition"
          :class="{
            'bg-secondary-500 text-secondary-800 hover:text-white hover:bg-secondary-800': page === currentPage,
            'text-white bg-secondary-800 hover:text-secondary-800 hover:bg-secondary-500': page !== currentPage,
          }"
          @click="changePage(page)"
        >
          {{ page }}
        </button>
      </li>
      <li>
        <button
          type="button"
          class="flex justify-center items-center flex-shrink-0 disabled:bg-secondary-200 disabled:cursor-not-allowed hover:disabled:bg-secondary-200 font-semibold h-8 w-8 rounded-full transition text-white cursor-pointer hover:bg-secondary-800 bg-secondary-800 dark:hover:bg-secondary-800"
          :disabled="currentPage === totalPages"
          @click="nextPage"
        >
          <component
            :is="props.pageLang === 'en' ? ChevronRight : ChevronLeft"
            :class="`text-secondary-50 ${currentPage === totalPages && 'text-secondary-600'}`"
            :size="14"
          ></component>
        </button>
      </li>
    </ul>
  </div>
</template>
<script lang="ts" setup>
// Import Vue Packages
import { computed } from 'vue'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

// Import Vue Custom Components
import { translate } from 'src/utils/translation'

//Props and Emits
const emits = defineEmits(['changePage'])
const props = defineProps<{
  totalRows: number,
  currentPage: any,
  itemsPerPage: any,
  pageLang?: string,
  pageDir?: string
  rows: any,
}>()

const totalPages = computed(() => Math.ceil(props.totalRows / props.itemsPerPage))

// Functions
const prevPage = (): void => {
  if (props.currentPage && props.currentPage > 1)
    emits('changePage', props.currentPage - 1)
}
const nextPage = (): void => {
  if (props.currentPage && props.currentPage < (totalPages.value ?? 0))
    emits('changePage', props.currentPage + 1)
}
const changePage = (page: number): void => emits('changePage', page)

const getCurrentStartRow = () => {
  return props.rows.length === 0
    ? 0
    : (props.currentPage - 1) * props.itemsPerPage + 1
}

const getCurrentEndRow = () => {
  return props.rows.length === 0
    ? 0
    : Math.min(
      props.currentPage * props.itemsPerPage,
      props.totalRows,
    )
}

const getMiddlePagination = () => {
  const values = [...Array.from({length: totalPages.value - 4}, (value, index) => 3 + index * 1)]
  if (values.length < 4) return values

  if (props.currentPage < values[0]) return [3, 4, 5]

  if (props.currentPage > values[values.length - 1]) return [
    values[values.length - 3],
    values[values.length - 2],
    values[values.length - 1],
  ]

  if (props.currentPage == values[0]) return [3, 4, 5]

  if (props.currentPage == values[values.length - 1]) return [
    values[values.length - 3],
    values[values.length - 2],
    values[values.length - 1],
  ]

  const currentIndex = values.findIndex((value) => value == props.currentPage)
  if (currentIndex == -1) return []

  return [values[currentIndex - 1], values[currentIndex], values[currentIndex + 1]]
}
</script>
